import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";

// Async thunk to save training category config
export const saveTrainingCategoryConfig = createAsyncThunk(
  "trainingCategoryConfig/saveTrainingCategoryConfig",
  async ({ configData, yearId, companyId, total_budget }, { rejectWithValue }) => {
    try {
      // Transform the config data to match the required format
      // Store both percentage and budget for each category in the database
      // Format: [{ category: "Category A", percentage: 50.0, budget: 500000.0 }, ...]
      const trainingCategoryConfig = configData.map(item => ({
        category: item.category,
        percentage: parseFloat(item.percentage) || 0,
        budget: parseFloat(item.budget) || 0
      }));

      const { data: responseData, error } = await supabase
        .from("configurations")
        .update({ training_category_config: trainingCategoryConfig, total_budget: total_budget})
        .eq("company_id", companyId)
        .eq("year_id", yearId)
        .select();
      
      if (error) {
        console.error("Save training category config error:", error);
        throw error;
      }
      return responseData;
    } catch (err) {
      console.error("Save training category config catch error:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async thunk to fetch training category config
export const fetchTrainingCategoryConfig = createAsyncThunk(
  "trainingCategoryConfig/fetchTrainingCategoryConfig",
  async ({ yearId, companyId }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("configurations")
        .select("training_category_config")
        .eq("company_id", companyId)
        .eq("year_id", yearId)
        .maybeSingle();
      
      if (error) {
        console.error("Fetch training category config error:", error);
        throw error;
      }
      return data?.training_category_config || [];
    } catch (err) {
      console.error("Fetch training category config catch error:", err);
      return rejectWithValue(err.message);
    }
  }
);

const trainingCategoryConfigSlice = createSlice({
  name: "trainingCategoryConfig",
  initialState: {
    config: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearConfig: (state) => {
      state.config = [];
    },
    updateConfig: (state, action) => {
      state.config = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Save Training Category Config cases
      .addCase(saveTrainingCategoryConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveTrainingCategoryConfig.fulfilled, (state, action) => {
        state.loading = false;
        // Update the config with the saved data
        if (action.payload && action.payload[0]?.training_category_config) {
          state.config = action.payload[0].training_category_config;
        }
      })
      .addCase(saveTrainingCategoryConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Training Category Config cases
      .addCase(fetchTrainingCategoryConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainingCategoryConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.config = action.payload;
      })
      .addCase(fetchTrainingCategoryConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearConfig, updateConfig } = trainingCategoryConfigSlice.actions;
export default trainingCategoryConfigSlice.reducer;
